import { useEffect, useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { pushTask } from "@/api/discrepancies";
import { Modal } from "@/components/ui/Modal";
import { errorKind } from "@/api/errors";
import { formatUsd } from "@/utils/ezyvetModels";
import type { ApiError } from "@/types";
import type { Discrepancy, PushTaskRequest, PushTaskResponse } from "@/types/ezyvet";

/** Same wording the backend uses for automatic alerts (mappers.task_payload). */
function defaults(d: Discrepancy): PushTaskRequest {
  let description = `VetGuard: possible missed charge, ${d.event_description} (about ${formatUsd(d.estimated_value_usd)}).`;
  if (d.recommended_action) description += ` ${d.recommended_action}`;
  return { title: `Unbilled: ${d.event_description}`.slice(0, 120), description };
}

function failureText(err: ApiError): string {
  switch (errorKind(err)) {
    case "auth":
      return "ezyVet refused the task. The API credentials need write access to tasks.";
    case "rate_limited":
      return "ezyVet's rate limit is reached. Try again in a minute.";
    case "network":
      return "Couldn't reach VetGuard. Check your connection and try again.";
    case "not_found":
    case "unavailable":
      return "Pushing tasks isn't available on this server yet.";
    default:
      return err.message;
  }
}

interface Props {
  discrepancy: Discrepancy;
  open: boolean;
  onClose: () => void;
  onPushed: (res: PushTaskResponse) => void;
}

/** Sends a flagged item back to ezyVet as a callback task (POST /v2/task). */
export function PushTaskModal({ discrepancy, open, onClose, onPushed }: Props) {
  const [form, setForm] = useState<PushTaskRequest>(() => defaults(discrepancy));

  useEffect(() => {
    if (open) setForm(defaults(discrepancy));
  }, [open, discrepancy]);

  const push = useMutation<PushTaskResponse, ApiError, PushTaskRequest>({
    mutationFn: (req) => pushTask(discrepancy.id, req),
    onSuccess: (res) => {
      onPushed(res);
      onClose();
    },
  });

  // A reopened modal shouldn't show the last attempt's error
  const { reset } = push;
  useEffect(() => {
    if (open) reset();
  }, [open, reset]);

  const valid = form.title.trim().length > 0 && form.description.trim().length > 0;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid && !push.isPending) push.mutate({ title: form.title.trim(), description: form.description.trim() });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Push task to ezyVet"
      footer={
        <>
          <button type="button" className="btn btn-sm" onClick={onClose} disabled={push.isPending}>
            Cancel
          </button>
          <button type="submit" form="push-task-form" className="btn btn-sm btn-primary" disabled={!valid || push.isPending}>
            {push.isPending ? "Sending…" : "Create task"}
          </button>
        </>
      }
    >
      <form id="push-task-form" onSubmit={submit}>
        <p className="muted">
          Creates a task on consult <span className="mono">#{String(discrepancy.consult?.id ?? "—")}</span> in ezyVet so
          the team can add the charge there.
        </p>
        <div className="field">
          <label htmlFor="task-title">Title</label>
          <input
            id="task-title"
            type="text"
            maxLength={120}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            data-autofocus
          />
        </div>
        <div className="field">
          <label htmlFor="task-description">Description</label>
          <textarea
            id="task-description"
            style={{ minHeight: 110, fontFamily: "inherit" }}
            maxLength={2000}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
        {push.error && (
          <div className="callout error" role="alert">
            {failureText(push.error)}
          </div>
        )}
      </form>
    </Modal>
  );
}
